import { db, storage } from "../lib/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

const CHART_LOCK_TIMEOUT_MS = 10 * 60 * 1000;

function blankIncident() {
  return {
    claimantType: "",
    claimantTypeOther: "",
    vendorName: "",
    description: "",
    conditions: {
      rain: false,
      dew: false,
      dry: false,
      dark: false,
      other: false,
      otherText: "",
    },
    causes: {
      slipFall: false,
      tripFall: false,
      vehicle: false,
      golfCart: false,
      struckByBall: false,
      foodBeverage: false,
      insectBiteSting: false,
      sunHeat: false,
      struckByAgainst: false,
    },
    golfCartNumber: "",
    golfCartDriverName: "",
    companyName: "",
    playerName: "",
    location: {
      hole: false,
      holeNumber: "",
      tee: false,
      fairway: false,
      green: false,
      clubhouse: false,
      hospitality: false,
      concessionArea: false,
      restroom: false,
      parkingLot: false,
      other: false,
      otherText: "",
    },
    witnessPresent: false,
    witnessName: "",
    witnessAddress: "",
    witnessPhone: "",
    photosTaken: false,
    firstAidGiven: false,
    otherInformation: "",
    otherInformationNA: false,
  };
}

function getLockRef(patientId) {
  return doc(db, "patientChartLocks", patientId);
}

function isLockExpired(lock) {
  return !lock?.expiresAtMs || Date.now() > Number(lock.expiresAtMs);
}

export async function claimPatientChartLock(patientId, sessionId, force = false) {
  const lockRef = getLockRef(patientId);

  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(lockRef);
    const existing = snap.exists() ? snap.data() : null;

    if (
      existing &&
      !force &&
      !isLockExpired(existing) &&
      existing.sessionId !== sessionId
    ) {
      return {
        ok: false,
        readOnly: true,
        lock: existing,
      };
    }

    const now = Date.now();

    const lock = {
      patientId,
      sessionId,
      lockedAtMs: now,
      lastActivityAtMs: now,
      expiresAtMs: now + CHART_LOCK_TIMEOUT_MS,
      lockedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
      label: "Field charting device",
    };

    transaction.set(lockRef, lock);

    return {
      ok: true,
      readOnly: false,
      lock,
    };
  });
}

export async function touchPatientChartLock(patientId, sessionId) {
  const lockRef = getLockRef(patientId);
  const snap = await getDoc(lockRef);

  if (!snap.exists()) return false;

  const lock = snap.data();

  if (lock.sessionId !== sessionId || isLockExpired(lock)) {
    return false;
  }

  const now = Date.now();

  await updateDoc(lockRef, {
    lastActivityAtMs: now,
    expiresAtMs: now + CHART_LOCK_TIMEOUT_MS,
    lastActivityAt: serverTimestamp(),
  });

  return true;
}

export async function releasePatientChartLock(patientId, sessionId) {
  const lockRef = getLockRef(patientId);

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(lockRef);
    if (!snap.exists()) return;

    const lock = snap.data();

    if (lock.sessionId === sessionId) {
      transaction.delete(lockRef);
    }
  });
}

export async function verifyPatientChartLock(patientId, sessionId) {
  const snap = await getDoc(getLockRef(patientId));

  if (!snap.exists()) return false;

  const lock = snap.data();

  return lock.sessionId === sessionId && !isLockExpired(lock);
}

export async function createPatient(data) {
  const docRef = await addDoc(collection(db, "patients"), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

export async function getPatientById(patientId) {
  const refDoc = doc(db, "patients", patientId);
  const snap = await getDoc(refDoc);

  if (!snap.exists()) return null;

  return {
    id: snap.id,
    ...snap.data(),
  };
}

export async function updatePatient(patientId, updates) {
  const refDoc = doc(db, "patients", patientId);
  await updateDoc(refDoc, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function createEncounter(patientId, data = {}) {
  const encountersRef = collection(db, "patients", patientId, "encounters");

  const payload = {
    status: "open",
    arrivalDate: new Date().toISOString().slice(0, 10),
    arrivalTime: new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    chiefComplaint: "",
    bed: "",
    seenAt: "HQ",
    source: "",
    providerName: "",
    reportCompletedBy: "",
    notes: "",
    medical: {
      complaintNotes: "",
      historyPresentIllness: "",
      assessment: "",
      treatmentRendered: "",
      bodyPartInjured: "",
      firstAidGiven: false,
      disposition: "",
      dispositionNotes: "",
      notes: "",
      followUpNotes: "",
    },
    vitals: [],
    documents: [],
    eventYear: 2026,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...data,
    incident: {
      ...blankIncident(),
      ...(data.incident || {}),
      conditions: {
        ...blankIncident().conditions,
        ...(data.incident?.conditions || {}),
      },
      causes: {
        ...blankIncident().causes,
        ...(data.incident?.causes || {}),
      },
      location: {
        ...blankIncident().location,
        ...(data.incident?.location || {}),
      },
    },
  };

  const refDoc = await addDoc(encountersRef, payload);

  await updateDoc(doc(db, "patients", patientId), {
    updatedAt: serverTimestamp(),
    lastEncounterAt: serverTimestamp(),
    encounterCount: Number(data.currentEncounterCount || 0) + 1,
  });

  return refDoc.id;
}

export async function listEncounters(patientId) {
  const encountersRef = collection(db, "patients", patientId, "encounters");
  const q = query(encountersRef, orderBy("createdAt", "desc"));
  const snap = await getDocs(q);

  return snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
}

export async function updateEncounter(patientId, encounterId, updates) {
  const refDoc = doc(db, "patients", patientId, "encounters", encounterId);

  await updateDoc(refDoc, {
    ...updates,
    updatedAt: serverTimestamp(),
  });

  await updateDoc(doc(db, "patients", patientId), {
    updatedAt: serverTimestamp(),
    lastEncounterAt: serverTimestamp(),
  });
}

export async function uploadEncounterDocument(patientId, encounterId, file) {
  if (!patientId) throw new Error("Missing patientId");
  if (!encounterId) throw new Error("Missing encounterId");
  if (!file) throw new Error("No file selected");

  const fileName = String(file.name || "document.pdf").replace(/[^\w.-]+/g, "_");
  const path = `patients/${patientId}/encounters/${encounterId}/${Date.now()}_${fileName}`;

  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, file, {
    contentType: file.type || "application/pdf",
  });

  const url = await getDownloadURL(storageRef);

  return {
    name: file.name,
    url,
    path,
    uploadedAt: new Date().toISOString(),
    type: file.type || "application/pdf",
  };
}

export { blankIncident };